import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ChevronLeft, FileText, RefreshCw, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const ACTION_COLOURS = {
  invite_user: 'bg-blue-100 text-blue-800',
  edit_user: 'bg-slate-100 text-slate-700',
  change_role: 'bg-purple-100 text-purple-800',
  suspend: 'bg-orange-100 text-orange-800',
  deactivate: 'bg-red-100 text-red-800',
  archive: 'bg-gray-200 text-gray-700',
  restore: 'bg-green-100 text-green-800',
  remove_from_building: 'bg-amber-100 text-amber-800',
  reassign_unit: 'bg-indigo-100 text-indigo-800',
  bulk_action: 'bg-pink-100 text-pink-800',
};

export default function AuditLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('all');
  const [targetEmail, setTargetEmail] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '300' });
    if (action !== 'all') params.set('action', action);
    const r = await fetch(`${API}/api/user-management/audit-logs?${params}`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) {
      const data = await r.json();
      let rows = data.logs || [];
      if (targetEmail) {
        rows = rows.filter((l) => (l.target_email || '').toLowerCase().includes(targetEmail.toLowerCase()));
      }
      setLogs(rows);
    }
    setLoading(false);
  }, [action, targetEmail]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const canSee = [
    'platform_owner', 'super_agent', 'agent_admin',
    'managing_agent', 'general_agent', 'trustee', 'chairman', 'property_manager',
  ].includes(user?.role);

  if (!canSee) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h2 className="text-xl font-semibold">Access restricted</h2>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="audit-log-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <FileText className="w-7 h-7 text-indigo-600" /> Audit Log
            </h1>
            <p className="text-gray-600 text-sm">Track every administrative action across the platform.</p>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="w-full sm:w-48" data-testid="audit-action-filter">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {Object.keys(ACTION_COLOURS).map((a) => (
                  <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by target email…"
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              className="flex-1"
              data-testid="audit-email-filter"
            />
            <Button variant="outline" onClick={fetchLogs} data-testid="audit-refresh">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Reason / Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">Loading…</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-gray-500">No audit entries.</TableCell></TableRow>
                ) : logs.map((log, idx) => (
                  <TableRow key={log.log_id || `${log.created_at || ''}-${idx}`} data-testid={`audit-row-${log.log_id || idx}`}>
                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{log.actor_email || log.actor || <span className="text-gray-400 italic">System</span>}</div>
                      <div className="text-xs text-gray-500">{log.actor_role || ''}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${ACTION_COLOURS[log.action] || 'bg-slate-100 text-slate-700'} font-normal`}>
                        {(log.action || '').replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div>{log.target_email || (log.target_user_id ? log.target_user_id : <span className="text-gray-400">—</span>)}</div>
                      <div className="text-xs text-gray-500">{log.target_role || ''}</div>
                    </TableCell>
                    <TableCell className="text-xs text-gray-600 max-w-md">
                      {log.reason && <div className="font-medium text-gray-800">{log.reason}</div>}
                      {log.details && Object.keys(log.details).length > 0 && (
                        <pre className="text-[10px] bg-gray-50 p-1 rounded mt-1 overflow-x-auto whitespace-pre-wrap break-all">
                          {JSON.stringify(log.details, null, 0)}
                        </pre>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
